import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CaseListFilters } from "@/api/endpoints/build-case-report-metadata";
import { Button } from "@/components/ui/button";
import { CASE_STATUSES, type CaseStatus } from "@/domain/case/case";

import { EMPTY_FILTERS } from "./filters-url";

export type ApplicationFiltersProps = {
  readonly filters: CaseListFilters;
  readonly onChange: (_filters: CaseListFilters) => void;
};

const FIELD_CLASS =
  "border-input bg-background h-9 rounded-md border px-2 text-sm";

/**
 * 件名検索の入力。
 *
 * 表示は**ローカルの下書き**を正本にする。絞り込みの正本は URL だが、URL 由来の
 * 値をそのまま value に流すと日本語入力が壊れる（変換中に親の再レンダで value が
 * 巻き戻り、変換バッファが再挿入されて「ああい」のように重複する）。
 *
 * 変換中（compositionstart〜compositionend）は親へ通知せず、確定時にまとめて
 * 通知する。URL を 1 文字ごとに書き換えないので再取得も減る。
 */
function KeywordInput({
  value,
  onCommit,
}: {
  readonly value: string;
  readonly onCommit: (_value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const isComposing = useRef(false);

  // クリアや保存ビューの適用など、外から変わったときは追従する
  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <input
      type="search"
      className={`${FIELD_CLASS} w-56`}
      value={draft}
      onChange={(event) => {
        setDraft(event.target.value);
        if (!isComposing.current) onCommit(event.target.value);
      }}
      onCompositionStart={() => {
        isComposing.current = true;
      }}
      onCompositionEnd={(event) => {
        isComposing.current = false;
        onCommit(event.currentTarget.value);
      }}
    />
  );
}

/**
 * 一覧の絞り込み。ステータス・期間は状態を持たず、変更を親（URL）へ委ねる。
 *
 * ステータスは単一選択にしている（複数選択は保存フィルタ＝ビューで扱う想定）。
 */
export function ApplicationFilters({
  filters,
  onChange,
}: ApplicationFiltersProps) {
  const { t } = useTranslation(["common", "domain"]);
  const hasCondition =
    filters.keyword.trim() !== "" ||
    filters.statuses.length > 0 ||
    filters.processModelId !== null ||
    filters.submittedFrom !== null ||
    filters.submittedTo !== null;

  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs">
        {t("common:myApplications.filters.keyword")}
        <KeywordInput
          value={filters.keyword}
          onCommit={(keyword) => onChange({ ...filters, keyword })}
        />
      </label>

      <label className="flex flex-col gap-1 text-xs">
        {t("common:myApplications.filters.status")}
        <select
          className={`${FIELD_CLASS} w-40`}
          value={filters.statuses[0] ?? ""}
          onChange={(event) =>
            onChange({
              ...filters,
              statuses:
                event.target.value === ""
                  ? []
                  : [event.target.value as CaseStatus],
            })
          }
        >
          <option value="">
            {t("common:myApplications.filters.allStatuses")}
          </option>
          {CASE_STATUSES.map((status) => (
            <option key={status} value={status}>
              {t(`domain:caseStatus.${status}`)}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-xs">
        {t("common:myApplications.filters.period")}
        <span className="flex items-center gap-1">
          <input
            type="date"
            className={FIELD_CLASS}
            value={filters.submittedFrom ?? ""}
            onChange={(event) =>
              onChange({
                ...filters,
                submittedFrom: event.target.value || null,
              })
            }
          />
          <span className="text-muted-foreground">–</span>
          <input
            type="date"
            className={FIELD_CLASS}
            value={filters.submittedTo ?? ""}
            onChange={(event) =>
              onChange({ ...filters, submittedTo: event.target.value || null })
            }
          />
        </span>
      </label>

      {hasCondition ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => onChange(EMPTY_FILTERS)}
        >
          {t("common:actions.clear")}
        </Button>
      ) : null}
    </div>
  );
}
